import { useState, useEffect, useRef } from 'react';
import { ChevronDown, Check, Plus, Loader2, Trash2 } from 'lucide-react';
import { gardensApi } from '../../services/api';
import { useDashboard } from '../../context/DashboardContext';
import { useGardenSettings } from '../../context/GardenSettingsContext';
import { useWizard } from '../../context/WizardContext';
import useGardenLoader from '../../hooks/useGardenLoader';
import DeleteGardenDialog from './DeleteGardenDialog';

/**
 * GardenSwitcher — Dropdown to switch between gardens.
 *
 * - Loads garden list via gardensApi.getAll() — the backend scopes the result
 *   to the caller's session/anon cookie (#312, SEC-04 #525)
 * - Active garden identified by gardenId (not name) for uniqueness
 * - Clicking a non-active garden loads it via useGardenLoader
 * - Clicking the active garden closes the dropdown without a reload
 * - Loading state: spinner per row, button disabled while switching
 * - Error state: inline error banner above the list, cleared on next open
 * - ESC and click-outside close the dropdown
 * - "Neuen Garten anlegen" opens the wizard (via setActiveView + wizard flow)
 *
 * Note: loadFromBackend writes canvas_objects to GardenSettingsContext (#428),
 * so the editor canvas refreshes whether the Editor is mounted now or remounts
 * later — no mount-effect coupling needed.
 */
const GardenSwitcher = () => {
  const { setActiveView } = useDashboard();
  const { settings, gardenId, resetSettings, setGardenId } = useGardenSettings();
  const { resetWizard } = useWizard();
  const { loadFromBackend } = useGardenLoader();

  const [open, setOpen] = useState(false);
  const [gardens, setGardens] = useState([]);
  const [switching, setSwitching] = useState(null); // id of the garden currently loading
  const [switchError, setSwitchError] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // {id, name} | null
  const containerRef = useRef(null);

  // Current garden name from settings (what the user sees as active)
  const currentName = settings?.name || 'Mein Garten';

  // Load gardens when the dropdown opens (error is cleared in the toggle
  // handler, so the effect body has no synchronous setState). The backend
  // scopes the list to the caller's session/anon cookie — no client-side
  // user filter needed (#312, SEC-04 #525).
  useEffect(() => {
    if (!open) return;
    gardensApi
      .getAll()
      .then((data) => setGardens(Array.isArray(data) ? data : []))
      .catch(() => setGardens([]));
  }, [open]);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const handleOutsideClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [open]);

  // Close on ESC
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  const handleNewGarden = () => {
    setOpen(false);
    // Reset all garden state so the editor starts with a clean slate and the
    // wizard opens. Without this, MainLayout would see the existing
    // gartenplaner_wizard_complete flag and skip the wizard entirely.
    resetWizard();
    resetSettings();
    setGardenId(null);
    localStorage.removeItem('gartenplaner_wizard_complete');
    localStorage.removeItem('gartenplaner_active_garden_id');
    localStorage.removeItem('gartenplaner_canvas');
    setActiveView('editor');
  };

  const handleDelete = async ({ id }) => {
    await gardensApi.delete(id);
    const remaining = gardens.filter((g) => g.id !== id);
    setGardens(remaining);

    if (id === gardenId) {
      if (remaining.length > 0) {
        await loadFromBackend(remaining[0].id);
      } else {
        resetWizard();
        resetSettings();
        setGardenId(null);
        localStorage.removeItem('gartenplaner_wizard_complete');
        localStorage.removeItem('gartenplaner_active_garden_id');
        localStorage.removeItem('gartenplaner_canvas');
        setActiveView('editor');
      }
    }
    setDeleteTarget(null);
    setOpen(false);
  };

  const handleSelect = async (garden) => {
    // Clicking the already-active garden just closes the dropdown
    if (garden.id === gardenId) {
      setOpen(false);
      return;
    }

    setSwitching(garden.id);
    setSwitchError(null);
    try {
      await loadFromBackend(garden.id);
      setOpen(false);
    } catch {
      setSwitchError('Der Garten konnte nicht geladen werden. Bitte versuche es erneut.');
    } finally {
      setSwitching(null);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() =>
          setOpen((prev) => {
            if (!prev) setSwitchError(null);
            return !prev;
          })
        }
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
      >
        <span className="max-w-[160px] truncate">{currentName}</span>
        <ChevronDown size={14} className={`text-gray-400 transition-transform duration-150 ${open ? 'rotate-180' : ''}`} />
      </button>

      <div
        role="listbox"
        aria-label="Garten wechseln"
        aria-hidden={!open}
        className={`absolute top-full left-0 mt-1 w-60 bg-white rounded-lg shadow-lg border border-gray-200 max-h-80 overflow-y-auto z-50 transition-all duration-150 origin-top ${
          open
            ? 'opacity-100 scale-y-100 pointer-events-auto'
            : 'opacity-0 scale-y-95 pointer-events-none'
        }`}
      >
        {switchError && (
          <div className="px-3 py-2 text-xs text-red-600 bg-red-50 border-b border-red-100">
            {switchError}
          </div>
        )}

        {gardens.length > 0 && (
          <ul className="py-1">
            {gardens.map((garden) => {
              const isActive = garden.id === gardenId;
              const isLoading = switching === garden.id;
              return (
                <li key={garden.id} className="group flex items-center">
                  <button
                    type="button"
                    role="option"
                    aria-selected={isActive}
                    disabled={switching !== null}
                    onClick={() => handleSelect(garden)}
                    className={`flex-1 flex items-center justify-between pl-3 pr-2 py-3 text-sm text-left transition-colors disabled:cursor-wait ${
                      isActive
                        ? 'bg-emerald-50 text-gray-900'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <span className="truncate">{garden.name}</span>
                    {isLoading && (
                      <Loader2 size={14} className="text-emerald-600 flex-shrink-0 animate-spin" />
                    )}
                    {isActive && !isLoading && (
                      <Check size={14} className="text-emerald-600 flex-shrink-0" />
                    )}
                  </button>
                  <button
                    type="button"
                    aria-label={`Garten löschen: ${garden.name}`}
                    disabled={switching !== null}
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteTarget({ id: garden.id, name: garden.name });
                    }}
                    className={`opacity-0 group-hover:opacity-100 focus:opacity-100 pr-3 py-3 text-gray-400 hover:text-red-500 transition-colors disabled:cursor-wait ${isActive ? 'bg-emerald-50' : ''}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        {gardens.length > 0 && <div className="border-t border-gray-100" />}

        <button
          type="button"
          onClick={handleNewGarden}
          className="w-full flex items-center gap-2 px-3 py-3 text-sm text-emerald-600 hover:bg-gray-50 transition-colors"
        >
          <Plus size={14} />
          <span>Neuen Garten anlegen</span>
        </button>
      </div>

      {deleteTarget && (
        <DeleteGardenDialog
          isOpen
          gardenName={deleteTarget.name}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => handleDelete(deleteTarget)}
        />
      )}
    </div>
  );
};

export default GardenSwitcher;
